import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import { Button } from '@ludolio/shared-ui';
import { Input } from '@ludolio/shared-ui';
import { Label } from '@ludolio/shared-ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Alert, AlertDescription } from '@ludolio/shared-ui';
import { LUDOLIO_PRIVACY_POLICY_URL, LUDOLIO_TERMS_OF_USE_URL } from '@ludolio/shared-ui';
import { CheckCircle2, AlertCircle } from 'lucide-react';

export function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [organizationName, _setOrganizationName] = useState('');
  const [checkingEmail, _setCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [step, setStep] = useState<'details' | 'code'>('details');
  const [code, setCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const { setAuth, clearError } = useAuthStore();

  const handleEmailBlur = async () => {
    // Email validation is now handled by the backend during OTP send
    // We just validate basic email format here
    if (!email || !email.includes('@')) {
      setEmailChecked(false);
      setEmailValid(false);
      setEmailError('');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(email)) {
      setEmailValid(true);
      setEmailChecked(true);
      setEmailError('');
    } else {
      setEmailValid(false);
      setEmailChecked(true);
      setEmailError('Please enter a valid email address.');
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setOtpError('');

    if (!emailValid) {
      setEmailError(
        emailError || 'Please use a verified teacher email address before continuing.'
      );
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiClient.sendOtp(email);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to send code');
      }

      if (!response.data.isNewUser) {
        setOtpError('An account with this email already exists. Please sign in instead.');
        return;
      }

      setStep('code');
    } catch (err: any) {
      let message = err.response?.data?.message || err.message || 'Failed to send code. Please try again.';

      // Check if it's a whitelist error
      if (message.includes('not authorized') || message.includes('whitelist')) {
        message = 'Your email is not authorized to register as a teacher. Please contact your librarian to be added to the teacher whitelist.';
      }

      setOtpError(message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setOtpError('');

    if (!firstName.trim() || !lastName.trim()) {
      setOtpError('Please enter your first and last name.');
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiClient.verifyOtp(email, code, firstName.trim(), lastName.trim());
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Invalid code');
      }

      const user = response.data.user;
      if (!user || user.role !== 'teacher') {
        await apiClient.logout();
        setOtpError('Access denied. Only instructor can access this portal.');
        return;
      }

      // New signups still complete first-login onboarding (consents) via the OnboardingGate
      setAuth({
        ...user,
        needsOnboarding: Boolean((response.data as any).needsOnboarding),
        needsOrgNoticeConsent: Boolean((response.data as any).needsOrgNoticeConsent),
      });
    } catch (err: any) {
      const message =
        err.response?.data?.message || err.message || 'Invalid code. Please try again.';
      setOtpError(message);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle as="h1" className="text-2xl font-bold text-center">Instructor Sign Up</CardTitle>
          <CardDescription className="text-center">
            Create an account to manage your students and classes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'details' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Jane"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={otpLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Smith"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={otpLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="instructor@university.edu"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailChecked(false);
                    setEmailValid(false);
                    setEmailError('');
                  }}
                  onBlur={handleEmailBlur}
                  required
                  disabled={otpLoading || checkingEmail}
                />
                {checkingEmail && (
                  <p className="text-sm text-muted-foreground">Checking email domain...</p>
                )}
                {emailChecked && emailValid && (
                  <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      Email verified! You'll be registered with {organizationName}
                    </AlertDescription>
                  </Alert>
                )}
                {emailChecked && !emailValid && emailError && (
                  <Alert className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <AlertDescription className="text-red-800 dark:text-red-200">
                      {emailError}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {otpError && (
                <Alert className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <AlertDescription className="text-red-800 dark:text-red-200">
                    {otpError}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={otpLoading || !emailValid || !firstName.trim() || !lastName.trim()}
              >
                {otpLoading ? 'Sending Code...' : 'Send Verification Code'}
              </Button>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">Already have an account? </span>
                <Link to="/login" className="font-medium text-[hsl(var(--color-primary-text))] hover:underline">
                  Sign In
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={otpLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the 6-digit code sent to {email}.
                </p>
              </div>

              {otpError && (
                <Alert className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <AlertDescription className="text-red-800 dark:text-red-200">
                    {otpError}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={otpLoading || code.length < 6}
              >
                {otpLoading ? 'Verifying...' : 'Verify & Create Account'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('details');
                  setCode('');
                  setOtpError('');
                }}
                className="w-full text-center text-sm text-muted-foreground hover:underline"
              >
                Change email
              </button>
            </form>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            By signing up, you agree to our{' '}
            <a
              href={LUDOLIO_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[hsl(var(--color-primary-text))] underline transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href={LUDOLIO_TERMS_OF_USE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[hsl(var(--color-primary-text))] underline transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
            >
              Terms of Use
            </a>.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}

